// DAO for mbank_statement_line. The fingerprint-keyed dedupe layer —
// all writes go through upsert() so re-ingesting the same statement is a
// no-op on existing rows (just touching last_seen fields).

import { BaseDao, type InvalidateFn, type SupabaseClient } from '../../base.ts';
import type {
  MbankStatementLineInsert,
  MbankStatementLineMatchState,
  MbankStatementLineRow,
} from '@shared/types/mbank.ts';

export class MbankStatementLineDao extends BaseDao<MbankStatementLineRow> {
  protected readonly dependentResources = ['mbank_statement_line', 'mbank_invoice_payment_state'];

  constructor(sb: SupabaseClient, invalidate?: InvalidateFn) {
    super(sb, 'mbank_statement_line', invalidate);
  }

  /**
   * Upsert a statement line. Key: (account_iban, fingerprint).
   *
   * - Insert if new
   * - Update last_seen_statement_id and last_seen_at on conflict
   * - Fill bank_reference on conflict when previously null (later report
   *   version may carry it when an earlier version didn't)
   *
   * Returns the final row (inserted or merged).
   */
  async upsertLine(input: MbankStatementLineInsert): Promise<MbankStatementLineRow> {
    // Supabase/PostgREST upsert uses ON CONFLICT on the specified target.
    // We use onConflict: 'account_iban,fingerprint' against the unique index.
    const { data, error } = await this.sb
      .from('mbank_statement_line')
      .upsert(input, { onConflict: 'account_iban,fingerprint' })
      .select('*')
      .single<MbankStatementLineRow>();
    if (error) throw error;
    this.handleInvalidation();
    return data;
  }

  /**
   * Lines already stored for `accountIban` under any of `bankReferences`, with
   * the report format of the statement that first brought them in. Used to
   * spot the same transaction arriving again in the other format (MT940 vs
   * camt053): different remittance text gives a different fingerprint, so the
   * upsert alone would insert it twice.
   */
  async findByBankReferences(
    accountIban: string,
    bankReferences: string[],
  ): Promise<Array<{ id: number; bank_reference: string; booking_date: string; amount: number; direction: string; report_format: string | null }>> {
    const out: Array<{ id: number; bank_reference: string; booking_date: string; amount: number; direction: string; report_format: string | null }> = [];
    const CHUNK = 200;
    for (let i = 0; i < bankReferences.length; i += CHUNK) {
      const { data, error } = await this.sb
        .from('mbank_statement_line')
        .select('id, bank_reference, booking_date, amount, direction, statement:first_seen_statement_id(report_format)')
        .eq('account_iban', accountIban)
        .in('bank_reference', bankReferences.slice(i, i + CHUNK));
      if (error) throw error;
      for (const r of (data ?? []) as unknown as Array<{
        id: number; bank_reference: string; booking_date: string; amount: number | string; direction: string;
        statement: { report_format: string | null } | null;
      }>) {
        out.push({
          id: r.id,
          bank_reference: r.bank_reference,
          booking_date: r.booking_date,
          amount: Number(r.amount),
          direction: r.direction,
          report_format: r.statement?.report_format ?? null,
        });
      }
    }
    return out;
  }

  /**
   * Statement lines booked on any of `dates` (optionally currency-filtered),
   * amount-agnostic — the manual-pairing picker's last-resort fallback when no
   * same-amount line exists in the window (fees/partial/bundled transfers or
   * missing statement coverage). The caller sorts by amount proximity.
   */
  async findNearDates(
    dates: string[],
    currency: string | null,
    limit = 50,
  ): Promise<MbankStatementLineRow[]> {
    if (dates.length === 0) return [];
    let q = this.sb
      .from('mbank_statement_line')
      .select('*')
      .in('booking_date', dates)
      .order('booking_date', { ascending: false })
      .limit(limit);
    if (currency != null) q = q.eq('currency', currency);
    const { data, error } = await q;
    if (error) throw error;
    return (data ?? []) as MbankStatementLineRow[];
  }

  async findUnmatched(limit = 200): Promise<MbankStatementLineRow[]> {
    const { data, error } = await this.sb
      .from('mbank_statement_line')
      .select('*')
      .in('match_state', ['pending', 'ambiguous'])
      .order('booking_date', { ascending: false })
      .limit(limit);
    if (error) throw error;
    return (data ?? []) as MbankStatementLineRow[];
  }

  /**
   * FA-import pairing candidates (finding fa_import_echo): statement lines whose
   * amount is in `amounts` AND whose booking_date is in `dates`. A SUPERSET query
   * — it doesn't correlate amount[i] with date[i] — the caller (the pure pairing
   * function) re-checks the exact (amount, date, direction, currency) predicates
   * per candidate. Both `.in()` lists are chunked at 200 and combined pairwise so
   * neither dimension can blow PostgREST's URL-length ceiling; results are
   * deduped by id across chunk pairs.
   */
  async findForPairing(amounts: number[], dates: string[]): Promise<MbankStatementLineRow[]> {
    if (amounts.length === 0 || dates.length === 0) return [];
    const CHUNK = 200;
    // Keyset-page every chunk. The (amount × booking_date) cross-product for a
    // full payment batch is far larger than PostgREST's ~1000-row default, and
    // an unranged select truncates SILENTLY — the dropped lines never reach
    // pairFaImportPayment, so their payments are misclassified as
    // 'no_line' (prod 2026-07-28: 181 of 192 "unpaired" payments had an exact
    // amount+direction+date+currency line that simply wasn't fetched). Same
    // hazard, same fix, as FakturowniaPaymentDao.listPaymentsNeedingClassification.
    const PAGE = 1000;
    const byId = new Map<number, MbankStatementLineRow>();
    for (let i = 0; i < amounts.length; i += CHUNK) {
      const amountChunk = amounts.slice(i, i + CHUNK);
      for (let j = 0; j < dates.length; j += CHUNK) {
        const dateChunk = dates.slice(j, j + CHUNK);
        let cursor = 0;
        for (;;) {
          let q = this.sb
            .from('mbank_statement_line')
            .select('*')
            .in('amount', amountChunk)
            .in('booking_date', dateChunk)
            .order('id', { ascending: true })
            .limit(PAGE);
          if (cursor) q = q.gt('id', cursor);
          const { data, error } = await q;
          if (error) throw error;
          const page = (data ?? []) as MbankStatementLineRow[];
          if (page.length === 0) break;
          for (const row of page) byId.set(row.id, row);
          cursor = page[page.length - 1].id;
          if (page.length < PAGE) break;
        }
      }
    }
    return [...byId.values()];
  }

  /**
   * Merge FA-import pairing suggestions onto a line's `match_alternatives`
   * (finding fa_import_echo). When `opts.promoteToAmbiguous` the line is also
   * pushed from 'pending' into 'ambiguous' with `match_reason:
   * 'fa_import_suggestion'` and `matched_at` cleared — an already-ambiguous /
   * partially_matched line keeps its own state+reason (caller decides; this DAO
   * just applies whatever the caller asks for). Plain single-row update.
   */
  async updateFaImportAlternatives(
    id: number,
    alternatives: unknown[],
    opts: { promoteToAmbiguous: boolean },
  ): Promise<void> {
    const update: Record<string, unknown> = { match_alternatives: alternatives };
    if (opts.promoteToAmbiguous) {
      update.match_state = 'ambiguous';
      update.match_reason = 'fa_import_suggestion';
      update.matched_at = null;
    }
    const { error } = await this.sb
      .from('mbank_statement_line')
      .update(update)
      .eq('id', id);
    if (error) throw error;
  }

  /**
   * Record the resolved counterparty company on a line WITHOUT touching its
   * match state — the trust-fold paths (manual pairing, interim pairToLine,
   * fa-import sweep) know the company from the payment side even when the
   * invoice matcher never ran (T7 remediation 2026-08-03).
   */
  async setCounterpartyCompany(id: number, companyId: number): Promise<void> {
    const { error } = await this.sb
      .from('mbank_statement_line')
      .update({ counterparty_company_id: companyId })
      .eq('id', id);
    if (error) throw error;
  }

  async setMatchState(
    id: number,
    matchState: MbankStatementLineMatchState,
    opts: {
      candidateInvoiceIds?: number[];
      matchAlternatives?: unknown;
      matchReason?: string | null;
      counterpartyCompanyId?: number | null;
    } = {},
  ): Promise<void> {
    // Both 'matched' and 'partially_matched' record a matched_at
    // timestamp (first human or auto intervention); 'pending'/
    // 'ambiguous' clear it.
    const isMatched = matchState === 'matched' || matchState === 'partially_matched';
    const update: Record<string, unknown> = {
      match_state: matchState,
      matched_at: isMatched ? new Date().toISOString() : null,
    };
    if (opts.candidateInvoiceIds !== undefined) {
      update.candidate_invoice_ids = opts.candidateInvoiceIds;
    }
    if (opts.matchAlternatives !== undefined) {
      update.match_alternatives = opts.matchAlternatives;
    }
    if (opts.matchReason !== undefined) {
      update.match_reason = opts.matchReason;
    }
    if (opts.counterpartyCompanyId !== undefined) {
      update.counterparty_company_id = opts.counterpartyCompanyId;
    }
    const { error } = await this.sb
      .from('mbank_statement_line')
      .update(update)
      .eq('id', id);
    if (error) throw error;
  }

  /** Send lines back to the matcher's queue: pending, no reason, no candidates
   *  (chunked `.in()` at 200). Used after the over-link repair removes a
   *  line's last link. */
  /** Every line that carries a bank reference, with the report format of the
   *  statement that first brought it in. Keyset-paged on id (response cap). */
  async listWithBankReferenceForRepair(): Promise<RepairReferenceLine[]> {
    const out: RepairReferenceLine[] = [];
    for (let after = 0; ; ) {
      const { data, error } = await this.sb
        .from('mbank_statement_line')
        .select('id, account_iban, bank_reference, booking_date, amount, direction, statement:first_seen_statement_id(report_format)')
        .not('bank_reference', 'is', null)
        .gt('id', after)
        .order('id', { ascending: true })
        .limit(1000);
      if (error) throw error;
      const rows = (data ?? []) as unknown as Array<Omit<RepairReferenceLine, 'report_format' | 'amount'> & {
        amount: number | string; statement: { report_format: string | null } | null;
      }>;
      for (const r of rows) {
        out.push({
          id: r.id, account_iban: r.account_iban, bank_reference: r.bank_reference, booking_date: r.booking_date,
          amount: Number(r.amount), direction: r.direction, report_format: r.statement?.report_format ?? null,
        });
      }
      if (rows.length < 1000) return out;
      after = rows[rows.length - 1].id;
    }
  }

  /** Title, counterparty and verdict of the given lines (chunks of 200). */
  async findForTitleRepair(ids: number[]): Promise<RepairTitleLine[]> {
    const out: RepairTitleLine[] = [];
    for (let i = 0; i < ids.length; i += 200) {
      const { data, error } = await this.sb
        .from('mbank_statement_line')
        .select('id, booking_date, counterparty_company_id, structured_invoice_number, unstructured_remittance, match_reason')
        .in('id', ids.slice(i, i + 200));
      if (error) throw error;
      out.push(...((data ?? []) as RepairTitleLine[]));
    }
    return out;
  }

  /** Pending lines the matcher parked as `invoice_already_fully_paid`, with
   *  their titles. Keyset-paged on id (response cap). */
  async listParkedAlreadyPaidForRepair(): Promise<Array<{ id: number; structured_invoice_number: string | null; unstructured_remittance: string | null }>> {
    const out: Array<{ id: number; structured_invoice_number: string | null; unstructured_remittance: string | null }> = [];
    for (let after = 0; ; ) {
      const { data, error } = await this.sb
        .from('mbank_statement_line')
        .select('id, structured_invoice_number, unstructured_remittance')
        .eq('match_state', 'pending')
        .eq('match_reason', 'invoice_already_fully_paid')
        .gt('id', after)
        .order('id', { ascending: true })
        .limit(1000);
      if (error) throw error;
      const rows = (data ?? []) as Array<{ id: number; structured_invoice_number: string | null; unstructured_remittance: string | null }>;
      out.push(...rows);
      if (rows.length < 1000) return out;
      after = rows[rows.length - 1].id;
    }
  }

  /** Partially matched lines with title, counterparty and verdict. Keyset-paged
   *  on id (response cap). */
  async listPartiallyMatchedForRepair(): Promise<RepairTitleLine[]> {
    const out: RepairTitleLine[] = [];
    for (let after = 0; ; ) {
      const { data, error } = await this.sb
        .from('mbank_statement_line')
        .select('id, booking_date, counterparty_company_id, structured_invoice_number, unstructured_remittance, match_reason')
        .eq('match_state', 'partially_matched')
        .gt('id', after)
        .order('id', { ascending: true })
        .limit(1000);
      if (error) throw error;
      const rows = (data ?? []) as RepairTitleLine[];
      out.push(...rows);
      if (rows.length < 1000) return out;
      after = rows[rows.length - 1].id;
    }
  }

  /** Deletes lines (their mbank_payment_links cascade). Callers detach every
   *  other reference first. */
  async deleteByIds(ids: number[]): Promise<void> {
    for (let i = 0; i < ids.length; i += 200) {
      const { error } = await this.sb.from('mbank_statement_line').delete().in('id', ids.slice(i, i + 200));
      if (error) throw error;
    }
  }

  /** `keepReason` leaves match_reason in place (the matcher overwrites it on
   *  its next pass) — for repairs that select lines by their verdict and must
   *  find a half-repaired line again. */
  async resetToPending(ids: number[], opts?: { keepReason?: boolean }): Promise<void> {
    for (let i = 0; i < ids.length; i += 200) {
      const { error } = await this.sb
        .from('mbank_statement_line')
        .update({
          match_state: 'pending',
          ...(opts?.keepReason ? {} : { match_reason: null }),
          matched_at: null,
          candidate_invoice_ids: [],
        })
        .in('id', ids.slice(i, i + 200));
      if (error) throw error;
    }
  }
}

export interface RepairReferenceLine {
  id: number;
  account_iban: string;
  bank_reference: string;
  booking_date: string;
  amount: number;
  direction: string;
  report_format: string | null;
}

export interface RepairTitleLine {
  id: number;
  booking_date: string;
  counterparty_company_id: number | null;
  structured_invoice_number: string | null;
  unstructured_remittance: string | null;
  match_reason: string | null;
}
